import { usePage, Link, router } from '@inertiajs/react'
import Avatar from '@/components/ui/Avatar'
import {
  Cog6ToothIcon as Cog6ToothSolid,
  ArrowRightStartOnRectangleIcon as ArrowRightSolid,
  UserCircleIcon as UserCircleSolid,
  SunIcon as SunSolid,
  MoonIcon as MoonSolid,
  ComputerDesktopIcon as ComputerSolid,
} from '@heroicons/react/20/solid'
import {
  ViewColumnsIcon,
  ShoppingBagIcon,

  DocumentTextIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Bars3Icon,
  Cog6ToothIcon,
  ChartBarIcon,
  ListBulletIcon,
  UserGroupIcon,
  BoltIcon,
  ClockIcon,
  BookOpenIcon,
  TruckIcon,
  CalendarIcon,
  CalculatorIcon,
  ArrowPathIcon,
  InboxIcon,
  InboxArrowDownIcon,
} from '@heroicons/react/24/outline'
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/16/solid'
import { useState, useEffect, type ReactNode } from 'react'
import clsx from 'clsx'
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
  DropdownLabel,
} from '@/components/catalyst/dropdown'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuth } from '@/hooks/useAuth'
import Logo from '@/components/ui/Logo'
import type { SharedPageProps, CurrentUser } from '@/types'

interface AppLayoutProps {
  children: ReactNode
}

const COLLAPSED_KEY = 'sidebar-collapsed'
const SIDEBAR_WIDTH = 248 // w-62 — wide enough for the longest Reports label
const DARK_MODE_KEY = 'dark-mode'
const COLLAPSED_WIDTH = 64 // w-16

// Each entry carries a permission key. The sidebar filters the list with
// `can(item.permission)` at render — entries the current role doesn't have
// access to are not shown at all (no blanket "you don't have permission"
// landing, no link in the menu). Permission strings match scope:action
// pairs in db/seeds/permissions_production_snapshot.rb.
//
// `Menu:purchase_orders` is in the permission catalog but is not assigned
// to any role in production, so we gate Purchase Orders on
// `PurchaseOrder:all` instead (the matching capability permission, granted
// to 8 of the 11 roles).
const navigation = [
  { name: 'Pipeline',         href: '/',                        icon: ViewColumnsIcon,       permission: 'Menu:pipeline' },
  { name: 'Customer Orders',  href: '/dashboard/index',         icon: ShoppingBagIcon,       permission: 'Menu:customer_orders' },
  { name: 'Invoices',         href: '/inertia_invoices',        icon: DocumentTextIcon,      permission: 'Menu:invoices' },
  { name: 'Purchase Orders',  href: '/inertia_purchase_orders', icon: DocumentDuplicateIcon, permission: 'PurchaseOrder:all' },
  { name: 'Vendor POs',       href: '/vpo/index',               icon: ClipboardDocumentListIcon, permission: 'Menu:po_search' },
  { name: 'Tasks',            href: '/tasks',                   icon: CheckCircleIcon,       permission: 'Menu:tasks' },
  // { name: 'Logistics',     href: '/logistics',               icon: GlobeAltIcon, permission: 'Menu:logistics' },
  { name: 'Admin',            href: '/admin/users',             icon: Cog6ToothIcon,         permission: 'Menu:admin_panel' },
] as const satisfies readonly { name: string; href: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; permission: string; count?: string }[]

// Reports menu — ports the legacy Reports dropdown (`if allowed.menu__reports?`
// + per-item role checks for Discrepancies and PYMS). Permissions matrix:
//   Menu:reports          → Admin, Sales, Sam's Club (gates the whole dropdown)
//   Report:discrepancies  → Admin, Sales        (per-item gate)
//   Report:pyms           → Admin, Sales        (per-item gate)
// Items with `permission: null` are visible to anyone with Menu:reports.
// Icons mirror the original Font Awesome glyphs (fa-list → ListBulletIcon,
// fa-child → UserGroupIcon, etc.) translated to Heroicons.
//
// `external: true` opens the href in a new tab (legacy endpoints that
// stream a file directly). Items pointing at /reports/:slug are real
// Inertia pages with a date-range form before the download.
const REPORTS_LINKS = [
  { name: 'Discrepancies Report',      href: '/reports/discrepancies',                   permission: 'Report:discrepancies' as string | null, icon: ListBulletIcon,     external: false },
  { name: 'Warehouse Monthly Units',   href: '/reports/warehouse_monthly_units',         permission: null,                                    icon: UserGroupIcon,      external: false },
  { name: 'Hershy Report',             href: '/reports/hershy',                          permission: null,                                    icon: BoltIcon,           external: false },
  { name: 'Orders Timing Report',      href: '/reports/orders_timing',                   permission: null,                                    icon: ClockIcon,          external: false },
  { name: 'Orders To Place Report',    href: '/export_orders_to_place_report',           permission: null,                                    icon: BookOpenIcon,       external: true },
  { name: 'Shipped vs Pending',        href: '/export_shipped_vs_pending_orders_report', permission: null,                                    icon: TruckIcon,          external: true },
  { name: 'Monthly Sales Report',      href: '/monthly_sales_report.xlsx',               permission: null,                                    icon: CalendarIcon,       external: true },
  { name: 'Positive Negative Report',  href: '/positive_negative_report.xlsx',           permission: null,                                    icon: CalculatorIcon,     external: true },
  { name: 'PYMS Report',               href: '/export_pyms_report',                      permission: 'Report:pyms' as string | null,          icon: ArrowPathIcon,      external: true },
  { name: 'Receivables Report',        href: '/export_receivables_report.xlsx',          permission: null,                                    icon: InboxIcon,          external: true },
  { name: 'Open Orders Report',        href: '/open_orders_reports',                     permission: null,                                    icon: InboxArrowDownIcon, external: true },
] as const satisfies readonly { name: string; href: string; permission: string | null; icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; external: boolean }[]

const DATABASE_LINKS = [
  { name: 'Customers', href: '/inertia_customers', initial: 'C' },
  { name: 'Vendors', href: '/inertia_vendors', initial: 'V' },
  { name: 'Companies', href: '/inertia_companies', initial: 'Co' },
  { name: 'Brands', href: '/inertia_brands', initial: 'B' },
  { name: 'Carriers', href: '/inertia_carriers', initial: 'Ca' },
  { name: 'Warehouses', href: '/inertia_warehouses', initial: 'W' },
  { name: 'Payment Options', href: '/inertia_payment_options', initial: 'P' },
  { name: 'Customer Payment Terms', href: '/inertia_customer_payment_terms', initial: 'CT' },
  { name: 'Vendor Payment Terms', href: '/inertia_vendor_payment_terms', initial: 'VT' },
]

function SidebarContent({
  collapsed,
  currentPath,
  user,
  themeMode,
  cycleTheme,
  onNavigate,
}: {
  collapsed: boolean
  currentPath: string
  user: CurrentUser
  themeMode: 'light' | 'dark' | 'system'
  cycleTheme: () => void
  onNavigate?: () => void
}) {
  const { can } = usePermissions()
  const { signOut } = useAuth()
  const canSettings = can('Menu:settings')
  const canAdminPanel = can('Menu:admin_panel')

  // Reports section is an inline accordion (not a popover dropdown) so the
  // expanded list lives inside the sidebar — visually anchored, scrolls
  // with the rest of nav, and reads as part of the same column instead of
  // a floating menu that looks bolted on.
  const [reportsOpen, setReportsOpen] = useState(false)
  const visibleReports = REPORTS_LINKS.filter((r) => !r.permission || can(r.permission))

  return (
    <nav className="flex h-full min-h-0 flex-col" data-testid="sidebar">
      {/* Header — App logo with dropdown */}
      <div className={clsx(
        'flex items-center border-b border-zinc-950/5 p-4 dark:border-white/5',
        collapsed ? 'justify-center' : ''
      )}>
        <Dropdown>
          <DropdownButton
            as="button"
            aria-label="App menu"
            className={clsx(
              'flex items-center gap-2 rounded-lg p-1 text-left hover:bg-zinc-100 dark:hover:bg-white/5',
              collapsed && 'justify-center'
            )}
          >
            <Logo />
            {!collapsed && (
              <>
                <span className="text-sm font-bold text-zinc-900 dark:text-white">Warehouse</span>
                <ChevronDownIcon className="h-4 w-4 text-zinc-400" />
              </>
            )}
          </DropdownButton>
          <DropdownMenu className="z-50 min-w-48" anchor="bottom start">
            {canAdminPanel && (
              <DropdownItem href="/admin/users" onClick={onNavigate}>
                <Cog6ToothSolid className="h-5 w-5 text-zinc-500" data-slot="icon" />
                <DropdownLabel>Admin Panel</DropdownLabel>
              </DropdownItem>
            )}
            {!canAdminPanel && (
              <DropdownItem href="/" onClick={onNavigate}>
                <ViewColumnsIcon className="h-5 w-5 text-zinc-500" data-slot="icon" />
                <DropdownLabel>Go to Dashboard</DropdownLabel>
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>

      {/* Navigation */}
      <nav aria-label="Sidebar" className="flex flex-1 flex-col overflow-y-auto px-4 py-4">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              {navigation.filter((item) => can(item.permission)).map((item) => {
                const isCurrent = currentPath === item.href || (item.href !== '/' && currentPath.startsWith(item.href))
                return (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      onClick={onNavigate}
                      preserveState
                      prefetch
                      title={collapsed ? item.name : undefined}
                      className={clsx(
                        'group relative flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                        collapsed && 'justify-center',
                        isCurrent
                          ? 'bg-gray-50 text-indigo-600 dark:bg-white/10 dark:text-indigo-400'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-indigo-400'
                      )}
                    >
                      <item.icon
                        aria-hidden="true"
                        className={clsx(
                          'size-6 shrink-0',
                          isCurrent ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 group-hover:text-indigo-600 dark:text-zinc-500 dark:group-hover:text-indigo-400'
                        )}
                      />
                      {!collapsed && item.name}
                      {!collapsed && item.name === 'Tasks' && user?.pendingTasksCount != null && user.pendingTasksCount > 0 && (
                        <span
                          aria-hidden="true"
                          className="ml-auto w-9 min-w-max rounded-full bg-indigo-50 px-2.5 py-0.5 text-center text-xs/5 font-medium whitespace-nowrap text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400"
                        >
                          {user.pendingTasksCount}
                        </span>
                      )}
                      {collapsed && item.name === 'Tasks' && user?.pendingTasksCount != null && user.pendingTasksCount > 0 && (
                        <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-indigo-500 px-1 text-[9px] font-bold text-white">
                          {user.pendingTasksCount}
                        </span>
                      )}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </li>
          {can('Menu:reports') && (
            <li data-testid="reports-section">
              <ul role="list" className="-mx-2 space-y-1">
                <li>
                  <button
                    type="button"
                    onClick={() => setReportsOpen((o) => !o)}
                    aria-expanded={reportsOpen}
                    aria-controls="reports-submenu"
                    data-testid="reports-trigger"
                    title={collapsed ? 'Reports' : undefined}
                    className={clsx(
                      'group relative flex w-full gap-x-3 rounded-md p-2 text-left text-sm/6 font-semibold',
                      collapsed && 'justify-center',
                      reportsOpen
                        ? 'bg-gray-50 text-indigo-600 dark:bg-white/10 dark:text-indigo-400'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-indigo-400',
                    )}
                  >
                    <ChartBarIcon
                      aria-hidden="true"
                      className={clsx(
                        'size-6 shrink-0',
                        reportsOpen
                          ? 'text-indigo-600 dark:text-indigo-400'
                          : 'text-gray-400 group-hover:text-indigo-600 dark:text-zinc-500 dark:group-hover:text-indigo-400',
                      )}
                    />
                    {!collapsed && (
                      <>
                        <span>Reports</span>
                        <ChevronDownIcon
                          aria-hidden="true"
                          className={clsx(
                            'ml-auto h-4 w-4 self-center text-zinc-400 transition-transform',
                            reportsOpen && 'rotate-180',
                          )}
                        />
                      </>
                    )}
                  </button>
                  {reportsOpen && (
                    <ul
                      id="reports-submenu"
                      role="list"
                      data-testid="reports-menu"
                      className={clsx(
                        'mt-1 space-y-0.5',
                        // Expanded sidebar — labeled list under the trigger.
                        !collapsed && 'border-l border-zinc-200 pl-3 ml-4 dark:border-zinc-700',
                        // Collapsed sidebar — icon-only column (no labels), so
                        // the popup width matches the icon column and stays
                        // visually anchored to the trigger above.
                        collapsed && 'flex flex-col items-center',
                      )}
                    >
                      {visibleReports.map((r) => {
                        const linkClass = clsx(
                          'group flex items-center gap-2 rounded-md py-1.5',
                          collapsed ? 'justify-center px-1.5' : 'pr-2 pl-2 text-xs/6',
                          'text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-indigo-400',
                        )
                        const iconEl = (
                          <r.icon
                            aria-hidden="true"
                            className="size-4 shrink-0 text-gray-400 group-hover:text-indigo-600 dark:text-zinc-500 dark:group-hover:text-indigo-400"
                          />
                        )
                        const label = !collapsed && <span>{r.name}</span>
                        return (
                          <li key={r.name}>
                            {r.external ? (
                              <a
                                href={r.href}
                                target="_blank"
                                rel="noopener"
                                role="menuitem"
                                title={collapsed ? r.name : undefined}
                                onClick={onNavigate}
                                className={linkClass}
                              >
                                {iconEl}
                                {label}
                              </a>
                            ) : (
                              <Link
                                href={r.href}
                                role="menuitem"
                                title={collapsed ? r.name : undefined}
                                onClick={onNavigate}
                                prefetch
                                className={linkClass}
                              >
                                {iconEl}
                                {label}
                              </Link>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </li>
              </ul>
            </li>
          )}
          {!collapsed && canSettings && (
            <li>
              <div className="text-xs/6 font-semibold text-gray-400">Database</div>
              <ul role="list" className="-mx-2 mt-2 space-y-1">
                {DATABASE_LINKS.map((item) => {
                  const isCurrent = currentPath.startsWith(item.href)
                  return (
                    <li key={item.name}>
                      <Link
                        href={item.href}
                        onClick={onNavigate}
                        prefetch
                        className={clsx(
                          'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                          isCurrent
                            ? 'bg-gray-50 text-indigo-600 dark:bg-white/10 dark:text-indigo-400'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-indigo-400'
                        )}
                      >
                        <span
                          className={clsx(
                            'flex size-6 shrink-0 items-center justify-center rounded-lg border bg-white text-[0.625rem] font-medium',
                            isCurrent
                              ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                              : 'border-gray-200 text-gray-400 group-hover:border-indigo-600 group-hover:text-indigo-600 dark:border-zinc-700 dark:text-zinc-500 dark:bg-zinc-800 dark:group-hover:border-indigo-400 dark:group-hover:text-indigo-400'
                          )}
                        >
                          {item.initial}
                        </span>
                        <span className="truncate">{item.name}</span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </li>
          )}
        </ul>
      </nav>

      {/* Footer — User dropdown */}
      <div className="border-t border-zinc-950/5 p-3 dark:border-white/5">
        <Dropdown>
          <DropdownButton
            as="button"
            aria-label="User menu"
            className={clsx(
              'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-zinc-100 dark:hover:bg-white/5',
              collapsed && 'justify-center px-0'
            )}
          >
            <Avatar name={user.name || user.email} size="md" />
            {!collapsed && (
              <>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-zinc-900 dark:text-white">{user.name || user.email}</p>
                  <p className="truncate text-xs text-zinc-500 dark:text-zinc-400">{user.role}</p>
                </div>
                <ChevronUpIcon className="h-4 w-4 shrink-0 text-zinc-400" />
              </>
            )}
          </DropdownButton>
          <DropdownMenu className="z-50 min-w-56" anchor="top start">
            <DropdownItem onClick={() => { router.visit('/profile') }}>
              <UserCircleSolid className="h-5 w-5 text-zinc-500" data-slot="icon" />
              <DropdownLabel>My Profile</DropdownLabel>
            </DropdownItem>
            <DropdownItem onClick={cycleTheme}>
              {themeMode === 'light' && <SunSolid className="h-5 w-5 text-zinc-500" data-slot="icon" />}
              {themeMode === 'dark' && <MoonSolid className="h-5 w-5 text-zinc-500" data-slot="icon" />}
              {themeMode === 'system' && <ComputerSolid className="h-5 w-5 text-zinc-500" data-slot="icon" />}
              <DropdownLabel>{themeMode === 'light' ? 'Light' : themeMode === 'dark' ? 'Dark' : 'System'}</DropdownLabel>
            </DropdownItem>
            <DropdownDivider />
            <DropdownItem onClick={signOut}>
              <ArrowRightSolid className="h-5 w-5 text-zinc-500" data-slot="icon" />
              <DropdownLabel>Sign Out</DropdownLabel>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
    </nav>
  )
}

export default function AppLayout({ children }: AppLayoutProps) {
  const { url, props } = usePage<SharedPageProps>()
  const user = props.currentUser

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(COLLAPSED_KEY) === 'true'
    }
    return false
  })
  const [mobileOpen, setMobileOpen] = useState(false)
  const [themeMode, setThemeMode] = useState<'light' | 'dark' | 'system'>(() => {
    if (typeof window === 'undefined') return 'light'
    const saved = localStorage.getItem(DARK_MODE_KEY)
    if (saved === 'dark' || saved === 'light' || saved === 'system') return saved
    if (saved === 'true') return 'dark'
    if (saved === 'false') return 'light'
    return 'light'
  })

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed))
  }, [collapsed])

  useEffect(() => {
    const root = document.documentElement
    const applyTheme = () => {
      const dark = themeMode === 'dark' || (themeMode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
      if (dark) root.classList.add('dark')
      else root.classList.remove('dark')
    }
    applyTheme()
    localStorage.setItem(DARK_MODE_KEY, themeMode)
    if (themeMode === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      mq.addEventListener('change', applyTheme)
      return () => mq.removeEventListener('change', applyTheme)
    }
  }, [themeMode])

  const cycleTheme = () => setThemeMode(prev => prev === 'light' ? 'dark' : prev === 'dark' ? 'system' : 'light')

  if (!user) return <>{children}</>

  return (
    <div
      className="relative isolate flex min-h-svh w-full bg-zinc-100 dark:bg-zinc-950"
      style={{ '--sidebar-w': `${collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH}px` } as React.CSSProperties}
    >
      {/* Desktop sidebar */}
      <div
        className="fixed inset-y-0 left-0 z-30 hidden border-r border-zinc-200 bg-white lg:block dark:border-zinc-700 dark:bg-zinc-900"
        style={{ width: collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH, transition: 'width 200ms' }}
      >
        <SidebarContent
          collapsed={collapsed}
          currentPath={url}
          user={user}
          themeMode={themeMode}
          cycleTheme={cycleTheme}
        />

      </div>

      {/* Floating collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="fixed top-7 z-40 hidden h-6 w-6 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-400 shadow-sm hover:bg-zinc-50 hover:text-zinc-600 lg:flex dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-500 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
        style={{ left: (collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH) - 4, transition: 'left 200ms' }}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        data-testid="collapse-toggle"
      >
        {collapsed ? (
          <ChevronRightIcon className="h-3.5 w-3.5" />
        ) : (
          <ChevronLeftIcon className="h-3.5 w-3.5" />
        )}
      </button>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="fixed inset-0 bg-black/30"
            onClick={() => setMobileOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl dark:bg-zinc-900">
            <SidebarContent
              collapsed={false}
              currentPath={url}
              user={user}
              themeMode={themeMode}
              cycleTheme={cycleTheme}
              onNavigate={() => setMobileOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Mobile header */}
      <div className="fixed top-0 left-0 right-0 z-20 flex h-14 items-center border-b border-zinc-200 bg-white px-4 lg:hidden dark:border-zinc-700 dark:bg-zinc-900">
        <button
          onClick={() => setMobileOpen(true)}
          className="rounded p-1.5 text-zinc-500 hover:bg-zinc-100"
          aria-label="Open navigation"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
        <div className="ml-3 flex items-center gap-2">
          <Logo size="sm" />
          <span className="text-sm font-bold text-zinc-900 dark:text-white">Warehouse</span>
        </div>
      </div>

      {/* Main content */}
      <main
        className="main-content flex min-w-0 flex-1 flex-col pt-14 lg:pt-0"
        style={{ transition: 'padding-left 200ms' }}
      >
        <div className="flex min-w-0 min-h-0 flex-1 flex-col lg:p-2">
          <div key={url} className="page-transition min-w-0 grow bg-white p-4 lg:rounded-lg lg:p-6 lg:shadow-xs lg:ring-1 lg:ring-zinc-950/5 dark:bg-zinc-900 dark:lg:ring-white/10">
            {children}
          </div>
        </div>
      </main>
    </div>
  )
}
